"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { buttonPrimary, buttonSecondary, card, colors, heroBackground, input, radius, shadow } from "@/styles/theme";
import TurnstileWidget, { type TurnstileWidgetHandle } from "@/components/TurnstileWidget";

// Bumped whenever the wording below changes - must match AuthEndpoints'
// own CurrentNdaVersion constant exactly, or the server rejects the
// signature as having been given against stale text (see that comment).
const NDA_VERSION = "2026-08-28";

type Step = "gate" | "form" | "nda" | "done";

// Caps the date picker at 18 years ago so most under-18 birthdates can't
// even be selected - a UX nicety, not the real enforcement (that's the
// server-side age check in AuthEndpoints.Register, since a date input's
// max is trivially bypassable). 18+, not 16+ (2026-08-29, Paul: resolve
// the age contradiction across the site to 18+ - TravelStory is treated
// as an adult service).
function eighteenYearsAgo(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  return date.toISOString().slice(0, 10);
}

export default function SignupPage() {
  return (
    <Suspense fallback={null}>
      <SignupForm />
    </Suspense>
  );
}

function SignupForm() {
  const [step, setStep] = useState<Step>("gate");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [ndaName, setNdaName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingApprovalMessage, setPendingApprovalMessage] = useState("");
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const searchParams = useSearchParams();
  const referralCode = searchParams.get("ref");
  const inviteToken = searchParams.get("invite");

  const [inviter, setInviter] = useState<{ displayName: string; username: string } | null>(null);
  const [inviteInvalid, setInviteInvalid] = useState(false);

  useEffect(() => {
    if (!inviteToken) {
      return;
    }

    fetch(`/api/friends/invite/${encodeURIComponent(inviteToken)}`)
      .then(async (res) => {
        if (!res.ok) {
          setInviteInvalid(true);
          return;
        }

        const data = await res.json();
        setEmail(data.email);
        setInviter({ displayName: data.inviterDisplayName, username: data.inviterUsername });
      })
      .catch(() => setInviteInvalid(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  const handleContinueToNda = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setStep("nda");
  };

  const handleSign = async () => {
    if (!ndaName.trim()) {
      return;
    }

    setMessage("");
    setLoading(true);

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          displayName,
          username,
          password,
          dateOfBirth,
          referralCode: referralCode || null,
          inviteToken: inviteToken || null,
          turnstileToken: turnstileRef.current?.getToken(),
          ndaSignedName: ndaName.trim(),
          ndaVersion: NDA_VERSION,
        }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => null);

        // Private beta - the account exists but can't log in until an
        // admin approves it, so there's nothing to redirect to yet.
        setPendingApprovalMessage(
          data?.message ??
            "Thanks for applying! Your account has been created, and you'll get access as soon " +
              "as your application is approved."
        );
        setStep("done");
        return;
      }

      const data = await response.json().catch(() => null);
      setMessage(data?.message ?? "Unable to submit your application");
      // Turnstile tokens are single-use - reset so a retry gets a fresh one.
      turnstileRef.current?.reset();
    } catch {
      setMessage("Unable to contact API");
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = {
    fontSize: "13px",
    fontWeight: 600,
    color: colors.textSecondary,
  };

  return (
    <div style={heroBackground}>
    <main
      style={{
        maxWidth: "440px",
        margin: "0 auto",
        padding: "100px 20px 40px",
      }}
    >
      <h1
        style={{
          textAlign: "center",
          marginBottom: "28px",
          fontSize: "28px",
          fontWeight: 800,
          backgroundImage: colors.gradient,
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        {step === "gate" && "Sign-ups are currently unavailable"}
        {step === "form" && "Apply for beta access"}
        {step === "nda" && "Confidentiality agreement"}
        {step === "done" && "Application received"}
      </h1>

      {step === "form" && referralCode && (
        <p
          style={{
            textAlign: "center",
            fontSize: "13px",
            color: colors.textSecondary,
            marginBottom: "12px",
          }}
        >
          Invited by <strong>{referralCode}</strong>
        </p>
      )}

      {step === "form" && inviter && (
        <p
          style={{
            textAlign: "center",
            fontSize: "13px",
            color: colors.textSecondary,
            marginBottom: "12px",
          }}
        >
          Invited by <strong>{inviter.displayName}</strong> (@{inviter.username})
        </p>
      )}

      {step === "form" && inviteInvalid && (
        <p
          style={{
            textAlign: "center",
            fontSize: "13px",
            color: colors.error,
            marginBottom: "12px",
          }}
        >
          This invite link is invalid or has expired - you can still apply below.
        </p>
      )}

      {step === "gate" && (
        <div style={{ ...card, textAlign: "center" }}>
          <p style={{ color: colors.textSecondary, lineHeight: 1.6, marginBottom: "20px" }}>
            TravelStory is in private beta testing, so we&apos;re not accepting open sign-ups
            right now. Would you like to apply to become a beta tester instead?
          </p>

          <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => setStep("form")}
              style={buttonPrimary}
            >
              Yes, I&apos;d like to apply
            </button>

            <Link href="/" style={buttonSecondary}>
              No thanks
            </Link>
          </div>
        </div>
      )}

      {step === "form" && (
        <form
          onSubmit={handleContinueToNda}
          style={{ ...card, display: "flex", flexDirection: "column", gap: "14px" }}
        >
          <div>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ ...input, marginTop: "6px" }}
            />
          </div>

          <div>
            <label style={labelStyle}>Display name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              style={{ ...input, marginTop: "6px" }}
            />
          </div>

          <div>
            <label style={labelStyle}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={30}
              pattern="[a-zA-Z0-9_.]+"
              title="Letters, numbers, '.' or '_' only"
              style={{ ...input, marginTop: "6px" }}
            />
          </div>

          <div>
            <label style={labelStyle}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ ...input, marginTop: "6px" }}
            />
          </div>

          <div>
            <label style={labelStyle}>Date of birth</label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
              max={eighteenYearsAgo()}
              style={{ ...input, marginTop: "6px" }}
            />
            <p style={{ fontSize: "12px", color: colors.textSecondary, marginTop: "4px" }}>
              You must be 18 or older to use TravelStory.
            </p>
          </div>

          <button
            type="submit"
            style={{ ...buttonPrimary, width: "100%", marginTop: "6px" }}
          >
            Continue to agreement
          </button>

          {message && (
            <p style={{ color: colors.error, fontSize: "14px", margin: 0 }}>
              {message}
            </p>
          )}
        </form>
      )}

      {step === "nda" && (
        <div style={{ ...card, display: "flex", flexDirection: "column", gap: "14px" }}>
          <p style={{ color: colors.textSecondary, fontSize: "13px", margin: 0 }}>
            Please read the agreement below, then type your full name to sign it.
          </p>

          <div
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              padding: "16px",
              maxHeight: "260px",
              overflowY: "auto",
              fontSize: "13px",
              lineHeight: 1.6,
              color: colors.textSecondary,
              background: "#fff",
            }}
          >
            <NdaText />
          </div>

          <div>
            <label style={labelStyle}>Type your full name to sign</label>
            <input
              type="text"
              value={ndaName}
              onChange={(e) => setNdaName(e.target.value)}
              placeholder="Full name"
              style={{ ...input, marginTop: "6px" }}
            />
          </div>

          <TurnstileWidget ref={turnstileRef} />

          <button
            onClick={handleSign}
            disabled={loading || !ndaName.trim()}
            style={{
              ...buttonPrimary,
              width: "100%",
              opacity: loading || !ndaName.trim() ? 0.6 : 1,
              cursor: loading || !ndaName.trim() ? "default" : "pointer",
            }}
          >
            {loading ? "Submitting..." : "I agree & sign"}
          </button>

          <button
            onClick={() => setStep("form")}
            type="button"
            style={{ background: "none", border: "none", color: colors.textSecondary, fontSize: "13px", cursor: "pointer" }}
          >
            &larr; Back to application
          </button>

          {message && (
            <p style={{ color: colors.error, fontSize: "14px", margin: 0 }}>
              {message}
            </p>
          )}
        </div>
      )}

      {step === "done" && (
        <div style={{ ...card, textAlign: "center" }}>
          <p style={{ fontSize: "28px", marginBottom: "12px" }}>&#9993;</p>
          <p style={{ fontWeight: 700, marginBottom: "8px" }}>You&apos;re on the list</p>
          <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
            {pendingApprovalMessage}
          </p>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: "20px", fontSize: "14px" }}>
        <Link
          href="/"
          style={{
            color: colors.accentStart,
            background: colors.cardBg,
            padding: "6px 14px",
            borderRadius: radius.pill,
            boxShadow: shadow.card,
          }}
        >
          &larr; Back to login
        </Link>
      </div>
    </main>
    </div>
  );
}

// Draft agreement (2026-08-28) - plain-English, matches the tone of
// /privacy. Bump NDA_VERSION above (and AuthEndpoints.CurrentNdaVersion)
// together with any wording change here.
function NdaText() {
  return (
    <>
      <p>
        <strong>TravelStory Beta Tester Confidentiality Agreement</strong>
      </p>
      <p>
        TravelStory is currently in private beta - not publicly released, and still under active
        development. In exchange for early access, you agree to the following.
      </p>
      <p>
        <strong>1. What this covers.</strong> &quot;Confidential Information&quot; means anything
        about TravelStory you learn through your beta access that isn&apos;t already public:
        unreleased features, how the app works internally, screenshots or recordings of the beta
        app, bugs and their details, and any other non-public material we share with you as a beta
        tester. It does not include information that&apos;s already public, that you already knew
        before getting beta access, or that you&apos;re required to disclose by law.
      </p>
      <p>
        <strong>2. Your obligations.</strong> While you have beta access, and for 12 months after
        it ends (or until TravelStory is publicly released, whichever comes first), you agree not
        to: share Confidential Information with anyone else, including posting screenshots, videos,
        or descriptions of unreleased features publicly or on social media; use Confidential
        Information for any purpose other than testing and giving us feedback; or attempt to copy,
        reverse-engineer, or build a competing product from what you learn during beta testing.
      </p>
      <p>
        <strong>3. Feedback.</strong> Any feedback, bug reports, or suggestions you give us about
        TravelStory are ours to use, modify, and act on freely, without any obligation to credit or
        compensate you.
      </p>
      <p>
        <strong>4. No warranty.</strong> The beta is provided as-is. It may contain bugs, may lose
        data, may change significantly, or may be discontinued at any time without notice.
        Don&apos;t rely on it for anything you can&apos;t afford to lose.
      </p>
      <p>
        <strong>5. No employment or business relationship.</strong> Being a beta tester
        doesn&apos;t make you an employee, contractor, partner, or agent of TravelStory - it&apos;s
        early access in exchange for feedback and confidentiality.
      </p>
      <p style={{ marginBottom: 0 }}>
        <strong>6. Governing law.</strong> This agreement is governed by the laws of England and
        Wales.
      </p>
    </>
  );
}
